import { VEBYearlyOpenAndCurrentEffectiveDate } from "../../../constants/VEBYearlyOpenAndCurrentDate";
import { Member, Relationship } from "../../../types/employee.types";
import calculateAge, { getAgeInDays } from "../age";

type EligibleDependents = {
	dependents: Member[];
	ineligibleRelationshipDependents: Member[];
	ineligibleDependents: Member[];
	coverage: string[];
};

export const getCoverage = (relationship: Relationship) => {
	if (relationship === "CHILD") {
		return "Employee & Dependents";
	} else if (relationship === "GRAND CHILD") {
		return "Employee & Dependents";
	} else if (relationship === "SPOUSE") {
		return "Employee & Spouse";
	} else if (relationship === "DOMESTIC PARTNER") {
		return "Employee & Spouse";
	} else if (relationship === "STEP CHILD") {
		return "Employee & Dependents";
	} else {
		return "Employee & Family";
	}
};

export const getKemperHospitalIndemnityCoverage = (relationship: Relationship) => {
	if (relationship === "CHILD" || relationship === "STEP CHILD") {
		return "Employee & Children";
	} else if (relationship === "SPOUSE") {
		return "Employee & Spouse";
	} else {
		return "Employee & Family";
	}
};

export const getBeamVisionCoverage = getKemperHospitalIndemnityCoverage;

export const getKemperHospitalEligibleDependents = (dependents: Member[]): EligibleDependents => {
	const coverage: string[] = ["Employee Only"];
	const eligibleDependents: Member[] = [];
	const eligibleRelationships: string[] = ["CHILD", "SPOUSE", "STEP CHILD"];
	const ineligibleRelationshipDependents: Member[] = [];
	const ineligibleDependents: Member[] = [];
	dependents.forEach((dependent: Member) => {
		const { date_of_birth, relationship } = dependent;
		if (relationship !== null && eligibleRelationships.includes(relationship)) {
			const dependentAge = calculateAge(
				date_of_birth,
				VEBYearlyOpenAndCurrentEffectiveDate.veb_current_effective_date_YYYYMMDD
			);
			// console.log("dependentAge", dependentAge);
			if (dependentAge <= 25 || relationship === "SPOUSE") {
				const coverageStr = getKemperHospitalIndemnityCoverage(relationship as Relationship);
				coverage.indexOf(coverageStr) === -1 ? coverage.push(coverageStr) : null;
				eligibleDependents.push(dependent);
			} else {
				ineligibleDependents.push(dependent);
			}
		} else {
			ineligibleRelationshipDependents.push(dependent);
		}
	});

	if (
		coverage.indexOf("Employee & Spouse") !== -1 &&
		coverage.indexOf("Employee & Children") !== -1 &&
		coverage.indexOf("Employee & Family") === -1
	) {
		coverage.push("Employee & Family");
	}
	return {
		dependents: eligibleDependents,
		coverage,
		ineligibleDependents,
		ineligibleRelationshipDependents
	};
};

export const getKemperWholeLifeEligibleDependents = (dependents: Member[]): EligibleDependents => {
	const coverage: string[] = ["Employee Only"];
	const eligibleDependents: Member[] = [];
	const eligibleRelationships: string[] = ["SPOUSE"];
	const ineligibleRelationshipDependents: Member[] = [];
	const ineligibleDependents: Member[] = [];
	dependents.forEach((dependent: Member) => {
		const { date_of_birth, relationship } = dependent;
		if (relationship !== null && eligibleRelationships.includes(relationship)) {
			const dependentAge = calculateAge(
				date_of_birth,
				VEBYearlyOpenAndCurrentEffectiveDate.veb_current_effective_date_YYYYMMDD
			);
			// console.log("dependentAge", dependentAge);
			if (dependentAge >= 18 && dependentAge <= 70) {
				const coverageStr = getKemperHospitalIndemnityCoverage(relationship as Relationship);
				coverage.indexOf(coverageStr) === -1 ? coverage.push(coverageStr) : null;
				eligibleDependents.push(dependent);
			} else {
				ineligibleDependents.push(dependent);
			}
		} else {
			ineligibleRelationshipDependents.push(dependent);
		}
	});
	return {
		dependents: eligibleDependents,
		coverage,
		ineligibleDependents,
		ineligibleRelationshipDependents
	};
};

export const getKemperWholeLifeEligibleDependentsV2 = (dependents: Member[]): EligibleDependents => {
	const coverage: string[] = ["Employee Only"];
	const eligibleDependents: Member[] = [];
	const eligibleRelationships: string[] = ["CHILD", "SPOUSE", "STEP CHILD"];
	const ineligibleRelationshipDependents: Member[] = [];
	const ineligibleDependents: Member[] = [];
	dependents.forEach((dependent: Member) => {
		const { date_of_birth, relationship } = dependent;
		if (relationship !== null && eligibleRelationships.includes(relationship)) {
			const dependentAge = calculateAge(
				date_of_birth,
				VEBYearlyOpenAndCurrentEffectiveDate.veb_current_effective_date_YYYYMMDD
			);
			// console.log("dependentAge", dependentAge);
			if (
				(((dependentAge >= 18 && dependentAge < 26) ||
					(getAgeInDays(date_of_birth) >= 30 && dependentAge < 26)) &&
					(relationship === "CHILD" || relationship === "STEP CHILD")) ||
				(dependentAge >= 18 && dependentAge <= 70 && relationship === "SPOUSE")
			) {
				const coverageStr = getKemperHospitalIndemnityCoverage(relationship as Relationship);
				coverage.indexOf(coverageStr) === -1 ? coverage.push(coverageStr) : null;
				eligibleDependents.push(dependent);
			} else {
				ineligibleDependents.push(dependent);
			}
		} else {
			ineligibleRelationshipDependents.push(dependent);
		}
	});
	return {
		dependents: eligibleDependents,
		coverage,
		ineligibleDependents,
		ineligibleRelationshipDependents
	};
};

export const getFiveStarEligibleDependents = (dependents: Member[], member: Member): EligibleDependents => {
	const memberAge = calculateAge(member?.date_of_birth, new Date());
	const coverage: string[] = memberAge >= 18 && memberAge <= 70 ? ["Employee Only"] : [];
	const eligibleDependents: Member[] = [];
	const eligibleRelationships: string[] = ["CHILD", "SPOUSE", "STEP CHILD"];
	let hasSpouse = false;
	let hasChildren = false;
	const ineligibleRelationshipDependents: Member[] = [];
	const ineligibleDependents: Member[] = [];
	dependents.forEach((dependent: Member) => {
		const { date_of_birth, relationship } = dependent;
		if (relationship !== null && eligibleRelationships.includes(relationship)) {
			const dependentAge = calculateAge(
				date_of_birth,
				VEBYearlyOpenAndCurrentEffectiveDate.veb_current_effective_date_YYYYMMDD
			);
			if (relationship === "SPOUSE") {
				// console.log("age", dependentAge);
			}
			// console.log("dependentAge", dependentAge);
			if (relationship === "SPOUSE" && dependentAge >= 18 && dependentAge <= 70) {
				hasSpouse = true;
				coverage.push("Employee & Spouse", "Spouse Only");
				eligibleDependents.push(dependent);
			} else if (relationship === "CHILD" || relationship === "STEP CHILD") {
				if (
					((dependentAge >= 1 && dependentAge < 19) ||
						(getAgeInDays(date_of_birth) >= 14 && dependentAge < 19)) &&
					dependent.marital_status != "MARRIED"
				) {
					hasChildren = true;
					if (coverage.includes("Employee & Children") === false) {
						coverage.push("Employee & Children");
					}
					eligibleDependents.push(dependent);
				} else {
					ineligibleDependents.push(dependent);
				}
			} else {
				ineligibleDependents.push(dependent);
			}
		} else {
			ineligibleRelationshipDependents.push(dependent);
		}
	});
	if (hasSpouse && hasChildren) {
		coverage.push("Spouse & Children", "Employee & Family");
	}
	// console.log("coverage", coverage);
	return {
		dependents: eligibleDependents,
		coverage,
		ineligibleDependents,
		ineligibleRelationshipDependents
	};
};

export const getKemperCancerEligibleDependents = getKemperHospitalEligibleDependents;
export const getDocAndRxDependents = getKemperHospitalEligibleDependents;
export const getBeamVisionEligibleDependents = getKemperHospitalEligibleDependents;
export const getMaxCareDentailEligibleDependents = getKemperHospitalEligibleDependents;
export const getKemperCriticalIllnessEligibleDependents = getKemperHospitalEligibleDependents;
export const getKemperAccidentEligibleDependents = getKemperHospitalEligibleDependents;
export const getBeazelyGroupLifeEligibleDependents = getKemperHospitalEligibleDependents;
